import React, { useState, useEffect, useRef, useCallback, useContext } from 'react';
import { Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBriefcase, faBuilding, faUser, faCalendarAlt,
  faChartLine, faSyncAlt, faCheckCircle, faExclamationTriangle,
  faArrowUp, faArrowDown
} from '@fortawesome/free-solid-svg-icons';
import API_CONFIG2 from '../../API_CONFIG/API_CONFIG2';
import Dexie from 'dexie'; 
import { PlacementContext } from '../layout/CunstomEntretiens';

// ============================================================
// CACHE DEXIE (24h) avec possibilité de récupérer le cache périmé
// ============================================================
const db = new Dexie('DashboardPlacementCacheDB');
db.version(1).stores({ dashboard: 'key, data, timestamp' });
const CACHE_DURATION = 24 * 60 * 60 * 1000;

async function getCachedDashboard(key, allowExpired = false) {
  try {
    const entry = await db.dashboard.get(key);
    if (!entry) return null;
    const age = Date.now() - entry.timestamp;
    if (!allowExpired && age > CACHE_DURATION) return null;
    return entry.data;
  } catch { return null; }
}
async function setCachedDashboard(key, data) {
  await db.dashboard.put({ key, data, timestamp: Date.now() });
}
async function getStaleDashboard(key) {
  return getCachedDashboard(key, true);
}

// ============================================================
// DÉDOUBLONNAGE DES REQUÊTES EN COURS (Map globale)
// ============================================================
const pendingFetches = new Map();

const API_URL = `${API_CONFIG2.apiUrl}placement/api_dashboard_placement.php?action=dashboard`;

// ============================================================
// HOOK POUR ANIMER LE COMPTEUR (inchangé)
// ============================================================
const useCountAnimation = (target, duration = 800, startDelay = 0) => {
  const [current, setCurrent] = useState(0);
  const frame = useRef(null);

  useEffect(() => {
    if (target === undefined || target === null) return;
    let start = null;
    const step = (timestamp) => {
      if (!start) start = timestamp;
      const progress = Math.min((timestamp - start) / duration, 1);
      setCurrent(Math.floor(progress * target));
      if (progress < 1) {
        frame.current = requestAnimationFrame(step);
      }
    };
    const timeout = setTimeout(() => {
      frame.current = requestAnimationFrame(step);
    }, startDelay);
    return () => {
      clearTimeout(timeout);
      if (frame.current) cancelAnimationFrame(frame.current);
    };
  }, [target, duration, startDelay]);

  return current;
};

// ============================================================
// FOND DE PARTICULES (inchangé)
// ============================================================
const ParticleCanvas = () => {
  const canvasRef = useRef(null);
  const particles = useRef([]);
  const animFrame = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let width, height;
    const resize = () => {
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = width;
      canvas.height = height;
      initParticles();
    };

    const initParticles = () => {
      particles.current = Array.from({ length: 60 }, () => ({
        x: Math.random() * width,
        y: Math.random() * height,
        vx: (Math.random() - 0.5) * 0.4,
        vy: (Math.random() - 0.5) * 0.4,
        radius: Math.random() * 2 + 1,
      }));
    };

    const draw = () => {
      ctx.clearRect(0, 0, width, height);
      const pts = particles.current;
      for (let i = 0; i < pts.length; i++) {
        const p = pts[i];
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0 || p.x > width) p.vx *= -1;
        if (p.y < 0 || p.y > height) p.vy *= -1;

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(59, 130, 246, 0.25)';
        ctx.fill();

        for (let j = i + 1; j < pts.length; j++) {
          const p2 = pts[j];
          const dx = p.x - p2.x;
          const dy = p.y - p2.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 140) {
            ctx.beginPath();
            ctx.moveTo(p.x, p.y);
            ctx.lineTo(p2.x, p2.y);
            ctx.strokeStyle = `rgba(59, 130, 246, ${0.12 * (1 - dist / 140)})`;
            ctx.lineWidth = 0.6;
            ctx.stroke();
          }
        }
      }
      animFrame.current = requestAnimationFrame(draw);
    };

    resize();
    window.addEventListener('resize', resize);
    animFrame.current = requestAnimationFrame(draw);

    return () => {
      window.removeEventListener('resize', resize);
      if (animFrame.current) cancelAnimationFrame(animFrame.current);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        zIndex: 0,
        pointerEvents: 'none',
        opacity: 0.6,
      }}
    />
  );
};

// ============================================================
// CARTE KPI (inchangée)
// ============================================================
const KpiCard = ({ icon, value, label, suffix = '', evolutionIcon = null, delay = 0 }) => {
  const animatedValue = useCountAnimation(value, 1000, delay + 200);
  return (
    <div className="glass-card stat-card fade-up" style={{ animationDelay: `${delay}ms` }}>
      <div className="stat-icon">
        <FontAwesomeIcon icon={icon} />
      </div>
      <div className="stat-content">
        <h3>
          {animatedValue}{suffix}
          {evolutionIcon && <FontAwesomeIcon icon={evolutionIcon} className="ms-2" style={{ color: evolutionIcon === faArrowUp ? '#10b981' : '#ef4444' }} />}
        </h3>
        <p>{label}</p>
      </div>
    </div>
  );
};

// ============================================================
// COMPOSANT PRINCIPAL AVEC DÉDOUBLONNAGE ET ANNULATION
// ============================================================
const DashboardPlacements = () => {
  const context = useContext(PlacementContext) || {};
  const { refreshKey, setSource } = context;

  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [toast, setToast] = useState({ show: false, message: '', variant: 'success' });
  const [dataSource, setDataSource] = useState('cache');
  const cardIndex = useRef(0);

  const abortControllerRef = useRef(null);
  const isMounted = useRef(true);

  // ============================================================
  // GESTION DES TOASTS
  // ============================================================
  const showToast = useCallback((message, variant = 'success', duration = 5000) => {
    setToast({ show: true, message, variant });
    setTimeout(() => {
      if (isMounted.current) setToast(prev => ({ ...prev, show: false }));
    }, duration);
  }, []);

  // ============================================================
  // FONCTION DE FETCH AVEC DÉDOUBLONNAGE + ANNULATION
  // ============================================================
  const fetchDashboard = useCallback(async (forceRefresh = false, retryCount = 0) => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    const cacheKey = 'dashboard_placement';
    const requestKey = forceRefresh ? `${cacheKey}_force` : cacheKey;

    if (pendingFetches.has(requestKey) && !forceRefresh) {
      try {
        const result = await pendingFetches.get(requestKey);
        if (isMounted.current && result) {
          setStats(result);
          setDataSource('cache (partagé)');
          if (setSource) setSource('cache (partagé)');
          setLoading(false);
          setRefreshing(false);
          setError('');
        }
        return;
      } catch {
        pendingFetches.delete(requestKey);
      }
    }

    if (retryCount > 5) {
      if (isMounted.current) {
        setLoading(false);
        setRefreshing(false);
        showToast('Erreur : trop de tentatives. Veuillez réessayer plus tard.', 'danger');
      }
      return;
    }

    if (forceRefresh) setRefreshing(true);
    else setLoading(true);
    setError('');

    const fetchPromise = (async () => {
      try {
        if (!forceRefresh) {
          const cached = await getCachedDashboard(cacheKey);
          if (cached && isMounted.current) {
            setStats(cached);
            setDataSource('cache');
            if (setSource) setSource('cache');
            setLoading(false);
            setRefreshing(false);
            return cached;
          }
        }

        const response = await fetch(API_URL, { signal: controller.signal });

        if (response.status === 429) {
          const stale = await getStaleDashboard(cacheKey);
          if (stale && isMounted.current) {
            setStats(stale);
            setDataSource('cache (périmé)');
            if (setSource) setSource('cache (périmé)');
            showToast('429 – Utilisation du cache périmé', 'warning');
            setLoading(false);
            setRefreshing(false);
            return stale;
          }
          const waitTime = Math.min(1000 * Math.pow(2, retryCount), 30000);
          await new Promise(resolve => setTimeout(resolve, waitTime));
          return fetchDashboard(forceRefresh, retryCount + 1);
        }

        if (!response.ok) {
          const stale = await getStaleDashboard(cacheKey);
          if (stale && isMounted.current) {
            setStats(stale);
            setDataSource('cache (périmé)');
            if (setSource) setSource('cache (périmé)');
            showToast(`Erreur réseau – Utilisation du cache`, 'warning');
            setLoading(false);
            setRefreshing(false);
            return stale;
          }
          throw new Error(`HTTP ${response.status}`);
        }

        const data = await response.json();
        if (data.status === 'success') {
          if (isMounted.current) {
            setStats(data.data);
            const source = forceRefresh ? 'live (forcé)' : 'live';
            setDataSource(source);
            if (setSource) setSource(source);
            await setCachedDashboard(cacheKey, data.data);
            if (forceRefresh) showToast('Données actualisées', 'success');
            setLoading(false);
            setRefreshing(false);
            setError('');
          }
          return data.data;
        } else {
          throw new Error(data.message || 'Erreur inconnue');
        }
      } catch (err) {
        if (err.name === 'AbortError') return null;
        const stale = await getStaleDashboard(cacheKey);
        if (stale && isMounted.current) {
          setStats(stale);
          setDataSource('cache (périmé)');
          if (setSource) setSource('cache (périmé)');
          showToast(`Erreur – Utilisation du cache : ${err.message}`, 'warning');
          setLoading(false);
          setRefreshing(false);
          return stale;
        }
        if (isMounted.current) {
          setError(err.message);
          showToast(err.message, 'danger');
          setLoading(false);
          setRefreshing(false);
        }
        throw err;
      }
    })();

    pendingFetches.set(requestKey, fetchPromise);
    try {
      const result = await fetchPromise;
      return result;
    } finally {
      pendingFetches.delete(requestKey);
      if (abortControllerRef.current === controller) {
        abortControllerRef.current = null;
      }
    }
  }, [setSource, showToast]);

  // ============================================================
  // CHARGEMENT INITIAL ET NETTOYAGE
  // ============================================================
  useEffect(() => {
    isMounted.current = true;
    fetchDashboard(false);
    return () => {
      isMounted.current = false;
      if (abortControllerRef.current) abortControllerRef.current.abort();
      pendingFetches.clear();
    };
  }, []);

  // ============================================================
  // RÉACTION AU REFRESH GLOBAL (via contexte)
  // ============================================================
  useEffect(() => {
    if (refreshKey && refreshKey > 0) {
      fetchDashboard(true);
    }
  }, [refreshKey, fetchDashboard]);

  // ============================================================
  // GESTIONNAIRE DE RAFRAÎCHISSEMENT LOCAL
  // ============================================================
  const handleRefresh = () => {
    if (!loading && !refreshing) {
      fetchDashboard(true);
    }
  };

  // ============================================================
  // AFFICHAGE DES ÉTATS DE CHARGEMENT / ERREUR
  // ============================================================
  if (loading && !stats) {
    return (
      <>
        <ParticleCanvas />
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="text-secondary mt-2">Chargement du tableau de bord...</p>
        </div>
      </>
    );
  }

  if (error && !stats) {
    return (
      <>
        <ParticleCanvas />
        <div className="text-center py-5 text-danger">
          <p>Erreur : {error}</p>
          <button className="monetra-btn primary" onClick={() => fetchDashboard(true)}>
            Réessayer
          </button>
        </div>
      </>
    );
  }

  if (!stats) return null;

  const evolutionNumber = parseInt(stats.evolution.replace(/[^0-9]/g, ''), 10) || 0;
  const evolutionIsPositive = stats.evolution.startsWith('+');
  const evolutionIcon = evolutionIsPositive ? faArrowUp : faArrowDown;
  const maxCount = Math.max(...stats.evolution_mensuelle.map(m => m.count), 1);

  cardIndex.current = 0;
  const getNextDelay = () => {
    cardIndex.current += 1;
    return cardIndex.current * 80;
  };

  // ============================================================
  // RENDU AVEC STYLES RESPONSIFS OPTIMISÉS
  // ============================================================
  return (
    <>
      <ParticleCanvas />
      <style>{`
        /* ============================================================
           STYLES RESPONSIFS – Dashboard Placements
           ============================================================ */
        :root {
          --accent-blue: #3b82f6;
          --accent-dark-blue: #1d4ed8;
          --accent-blue-glow: rgba(59, 130, 246, 0.35);
          --bg-primary: #0b1428;
          --card-bg: rgba(20, 40, 80, 0.5);
          --card-border: rgba(59, 130, 246, 0.15);
          --text-primary: #e0e7ff;
          --text-secondary: #94a3b8;
          --text-muted: #64748b;
          --radius: 18px;
          --shadow: 0 12px 40px rgba(0, 0, 0, 0.5);
        }

        .dashboard-container {
          position: relative;
          z-index: 1;
          min-height: 100vh;
          padding: clamp(0.75rem, 2vw, 2rem);
          color: var(--text-primary);
          font-family: 'Inter', system-ui, -apple-system, sans-serif;
          background: radial-gradient(circle at 20% 30%, #132044, #0a0f1f);
        }

        .fade-up {
          opacity: 0;
          animation: fadeInUp 0.5s ease forwards;
        }
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .glass-card {
          background: var(--card-bg);
          backdrop-filter: blur(16px);
          -webkit-backdrop-filter: blur(16px);
          border: 1px solid var(--card-border);
          border-radius: var(--radius);
          padding: clamp(1rem, 2vw, 1.8rem);
          box-shadow: var(--shadow);
          transition: all 0.25s ease;
          margin-bottom: clamp(1rem, 2vw, 1.8rem);
        }
        .glass-card:hover {
          border-color: rgba(59, 130, 246, 0.3);
          box-shadow: 0 12px 40px rgba(59, 130, 246, 0.08);
          transform: translateY(-2px);
        }

        .badge-source {
          font-size: clamp(0.6rem, 0.9vw, 0.75rem);
          padding: 4px 12px;
          border-radius: 40px;
          background: rgba(59,130,246,0.15);
          color: var(--accent-blue);
          border: 1px solid rgba(59,130,246,0.12);
          white-space: nowrap;
          font-weight: 500;
        }
        .badge-source.live {
          background: rgba(46,204,113,0.15);
          color: #2ecc71;
          border-color: rgba(46,204,113,0.12);
        }
        .badge-source.warning {
          background: rgba(245,158,11,0.15);
          color: #f59e0b;
          border-color: rgba(245,158,11,0.12);
        }

        .monetra-btn {
          padding: 8px 16px;
          border-radius: 30px;
          border: 1px solid rgba(255,255,255,0.06);
          background: rgba(255,255,255,0.04);
          color: var(--text-primary);
          transition: all 0.25s ease;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-weight: 500;
          cursor: pointer;
          font-size: clamp(0.75rem, 1vw, 0.9rem);
          white-space: nowrap;
          min-height: 44px;
          touch-action: manipulation;
        }
        .monetra-btn:hover:not(:disabled) {
          background: rgba(255,255,255,0.08);
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(0,0,0,0.2);
        }
        .monetra-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
          transform: none;
        }
        .monetra-btn.primary {
          background: var(--accent-blue);
          color: white;
          border: none;
          box-shadow: 0 6px 20px var(--accent-blue-glow);
        }
        .monetra-btn.primary:hover:not(:disabled) {
          background: var(--accent-dark-blue);
          box-shadow: 0 6px 24px rgba(59, 130, 246, 0.4);
        }

        .stat-card {
          display: flex;
          align-items: center;
          gap: clamp(0.8rem, 1.5vw, 1.2rem);
        }
        .stat-icon {
          width: clamp(44px, 6vw, 54px);
          height: clamp(44px, 6vw, 54px);
          border-radius: 16px;
          background: linear-gradient(135deg, rgba(59,130,246,0.2), rgba(29,78,216,0.1));
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: clamp(1.2rem, 2vw, 1.6rem);
          color: var(--accent-blue);
          backdrop-filter: blur(4px);
          flex-shrink: 0;
        }
        .stat-content h3 {
          font-size: clamp(1.4rem, 3vw, 2.2rem);
          font-weight: 700;
          margin: 0;
          line-height: 1.2;
          background: linear-gradient(180deg, #fff, #a5b4fc);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }
        .stat-content p {
          margin: 0;
          color: var(--text-secondary);
          font-size: clamp(0.75rem, 1vw, 0.9rem);
        }

        .chart-bar {
          display: flex;
          align-items: flex-end;
          gap: 4px;
          height: clamp(100px, 20vh, 140px);
          margin-top: 10px;
        }
        .chart-bar-item {
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 4px;
        }
        .chart-bar-fill {
          width: 100%;
          background: linear-gradient(180deg, var(--accent-blue), var(--accent-dark-blue));
          border-radius: 8px 8px 4px 4px;
          min-height: 4px;
          height: 0;
          animation: growBar 0.8s ease forwards;
          box-shadow: 0 0 8px rgba(59,130,246,0.3);
        }
        @keyframes growBar {
          to { height: var(--target-height); }
        }
        .chart-bar-fill:hover {
          opacity: 1;
          filter: brightness(1.2);
        }
        .chart-bar-label {
          font-size: clamp(0.55rem, 0.8vw, 0.7rem);
          color: var(--text-secondary);
          text-align: center;
        }

        .ranking-table, .recent-table {
          width: 100%;
          border-collapse: collapse;
          font-size: clamp(0.7rem, 1vw, 0.85rem);
        }
        .ranking-table th, .recent-table th {
          text-align: left;
          padding: 8px 6px;
          color: var(--text-secondary);
          font-weight: 500;
          border-bottom: 1px solid rgba(255,255,255,0.06);
          font-size: clamp(0.6rem, 0.8vw, 0.75rem);
        }
        .ranking-table td, .recent-table td {
          padding: 8px 6px;
          border-bottom: 1px solid rgba(255,255,255,0.02);
        }
        .ranking-table tr:hover td, .recent-table tr:hover td {
          background: rgba(255,255,255,0.03);
        }

        .grid-4col {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(clamp(160px, 22vw, 220px), 1fr));
          gap: clamp(12px, 2vw, 20px);
          margin-bottom: clamp(1rem, 2vw, 1.5rem);
        }
        .grid-2col-wide {
          display: grid;
          grid-template-columns: 2fr 1fr;
          gap: clamp(12px, 2vw, 20px);
        }
        .grid-2col {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: clamp(12px, 2vw, 20px);
        }

        .flex-between {
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 12px;
        }
        .gap-2 { gap: 8px; }
        .mb-3 { margin-bottom: clamp(1rem, 2vw, 1.8rem); }
        .text-secondary { color: var(--text-secondary); }
        .text-muted { color: var(--text-muted); }
        .text-center { text-align: center; }
        .fw-bold { font-weight: 600; }
        .small { font-size: 0.85rem; }
        .display-6 {
          font-size: clamp(1.6rem, 4vw, 2.2rem);
          font-weight: 700;
        }
        .d-flex { display: flex; }
        .flex-wrap { flex-wrap: wrap; }
        .align-items-center { align-items: center; }
        .justify-content-between { justify-content: space-between; }
        .ms-2 { margin-left: 0.5rem; }
        .text-accent { color: var(--accent-blue); }

        .toast-container {
          position: fixed;
          top: clamp(12px, 2vh, 24px);
          right: clamp(12px, 2vw, 24px);
          z-index: 9999;
          display: flex;
          flex-direction: column;
          gap: 12px;
          max-width: 440px;
        }
        .toast-item {
          background: rgba(10, 15, 31, 0.85);
          backdrop-filter: blur(24px);
          -webkit-backdrop-filter: blur(24px);
          border: 1px solid rgba(59, 130, 246, 0.2);
          border-radius: var(--radius);
          padding: 12px 18px;
          color: var(--text-primary);
          display: flex;
          align-items: center;
          gap: 14px;
          box-shadow: 0 8px 32px rgba(0,0,0,0.5);
          animation: slideIn 0.4s ease;
          font-size: clamp(0.85rem, 1.2vw, 1rem);
        }
        .toast-item.success { border-left: 4px solid #10b981; }
        .toast-item.warning { border-left: 4px solid #f59e0b; }
        .toast-item.danger { border-left: 4px solid #ef4444; }
        @keyframes slideIn {
          from { opacity: 0; transform: translateX(40px) scale(0.96); }
          to { opacity: 1; transform: translateX(0) scale(1); }
        }
        .btn-close-custom {
          background: transparent;
          border: none;
          color: var(--text-muted);
          cursor: pointer;
          font-size: 1.2rem;
          padding: 0 4px;
          margin-left: auto;
          transition: color 0.2s;
        }
        .btn-close-custom:hover { color: var(--text-primary); }

        /* ============================================================
           RESPONSIVE BREAKPOINTS
           ============================================================ */
        @media (max-width: 992px) {
          .grid-2col-wide {
            grid-template-columns: 1fr;
          }
        }
        @media (max-width: 768px) {
          .dashboard-container { padding: 12px; }
          .glass-card { padding: 1rem; }

          .flex-between {
            flex-direction: column;
            align-items: stretch;
            gap: 12px;
          }
          .flex-between > div:first-child {
            text-align: center;
          }

          .monetra-btn {
            font-size: 0.75rem;
            padding: 6px 12px;
            min-height: 38px;
            flex: 1 0 auto;
            justify-content: center;
          }
          .badge-source {
            font-size: 0.65rem;
          }

          .grid-2col {
            grid-template-columns: 1fr;
          }
          .grid-4col {
            grid-template-columns: 1fr 1fr;
          }

          .chart-bar {
            height: 90px;
          }
          .stat-icon {
            width: 40px;
            height: 40px;
            font-size: 1.2rem;
          }
          .stat-content h3 {
            font-size: 1.4rem;
          }
          .ranking-table, .recent-table {
            font-size: 0.7rem;
          }
          .ranking-table th, .recent-table th {
            font-size: 0.6rem;
          }
        }
        @media (max-width: 480px) {
          .grid-4col {
            grid-template-columns: 1fr 1fr;
            gap: 8px;
          }
          .stat-card {
            gap: 0.6rem;
          }
          .stat-icon {
            width: 34px;
            height: 34px;
            font-size: 1rem;
          }
          .stat-content h3 {
            font-size: 1.2rem;
          }
          .stat-content p {
            font-size: 0.65rem;
          }
          .monetra-btn {
            font-size: 0.65rem;
            padding: 4px 10px;
            min-height: 32px;
          }
          .chart-bar {
            height: 70px;
          }
          .chart-bar-label {
            font-size: 0.5rem;
          }
          .toast-item {
            padding: 10px 14px;
            font-size: 0.8rem;
          }
        }
      `}</style>

      <div className="dashboard-container">
        {/* En-tête */}
        <div className="flex-between mb-3 fade-up" style={{ animationDelay: '0ms' }}>
          <div>
            <h1 className="display-6 fw-bold mb-1">📊 Dashboard Placements</h1>
            <p className="text-secondary m-0" style={{ fontSize: 'clamp(0.75rem, 1.2vw, 0.95rem)' }}>
              Analyse des placements candidats
            </p>
          </div>
          <div className="d-flex flex-wrap align-items-center gap-2">
            <span className={`badge-source ${dataSource === 'live' ? 'live' : dataSource === 'cache (périmé)' ? 'warning' : ''}`}>
              {dataSource === 'cache' ? '📦 Cache' :
               dataSource === 'cache (périmé)' ? '⚠️ Cache périmé' :
               dataSource === 'cache (partagé)' ? '🔄 Partage' :
               dataSource === 'live' ? '🔄 Live' :
               dataSource === 'live (forcé)' ? '🔄 Forcé' : '📦 Cache'}
            </span>
            <button
              className="monetra-btn primary"
              onClick={handleRefresh}
              disabled={loading || refreshing}
            >
              <FontAwesomeIcon icon={faSyncAlt} spin={loading || refreshing} />
              {refreshing ? 'Actualisation...' : 'Actualiser'}
            </button>
          </div>
        </div>

        {/* Cartes KPI */}
        <div className="grid-4col mb-3">
          <KpiCard
            icon={faBriefcase}
            value={stats.total}
            label="Total placements"
            delay={getNextDelay()}
          />
          <KpiCard
            icon={faChartLine}
            value={evolutionNumber}
            suffix="%"
            label="Évolution vs mois dernier"
            evolutionIcon={evolutionIcon}
            delay={getNextDelay()}
          />
          <KpiCard
            icon={faBuilding}
            value={stats.top_entreprises.length > 0 ? stats.top_entreprises[0].total : 0}
            label={`Top entreprise (${stats.top_entreprises.length > 0 ? stats.top_entreprises[0].nomEntreprise : '—'})`}
            delay={getNextDelay()}
          />
          <KpiCard
            icon={faUser}
            value={stats.derniers_placements.length > 0 ? 1 : 0}
            label="Dernier placement"
            delay={getNextDelay()}
          />
        </div>

        {/* Graphique + Top entreprises */}
        <div className="grid-2col-wide mb-3">
          <div className="glass-card fade-up" style={{ animationDelay: `${getNextDelay()}ms` }}>
            <h5 className="mb-2 d-flex align-items-center gap-2" style={{ fontSize: 'clamp(0.95rem, 1.3vw, 1.1rem)' }}>
              <FontAwesomeIcon icon={faChartLine} className="text-accent" />
              Évolution mensuelle
            </h5>
            <div className="chart-bar">
              {stats.evolution_mensuelle.map((item, idx) => {
                const heightPercent = Math.max((item.count / maxCount) * 100, 4);
                return (
                  <div key={idx} className="chart-bar-item">
                    <div
                      className="chart-bar-fill"
                      style={{ '--target-height': `${heightPercent}px` }}
                    />
                    <span className="chart-bar-label">{item.month}</span>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="glass-card fade-up" style={{ animationDelay: `${getNextDelay()}ms` }}>
            <h5 className="mb-3 d-flex align-items-center gap-2" style={{ fontSize: 'clamp(0.95rem, 1.3vw, 1.1rem)' }}>
              <FontAwesomeIcon icon={faBuilding} className="text-accent" />
              Top entreprises
            </h5>
            {stats.top_entreprises.length === 0 ? (
              <p className="text-secondary">Aucune donnée</p>
            ) : (
              <table className="ranking-table">
                <tbody>
                  {stats.top_entreprises.map((item, idx) => (
                    <tr key={idx}>
                      <td>{idx + 1}.</td>
                      <td>{item.nomEntreprise}</td>
                      <td className="text-end fw-bold" style={{ color: 'var(--accent-blue)' }}>{item.total}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* Top offres + Derniers placements */}
        <div className="grid-2col">
          <div className="glass-card fade-up" style={{ animationDelay: `${getNextDelay()}ms` }}>
            <h5 className="mb-3 d-flex align-items-center gap-2" style={{ fontSize: 'clamp(0.95rem, 1.3vw, 1.1rem)' }}>
              <FontAwesomeIcon icon={faBriefcase} className="text-accent" />
              Top offres
            </h5>
            {stats.top_offres.length === 0 ? (
              <p className="text-secondary">Aucune donnée</p>
            ) : (
              <table className="ranking-table">
                <tbody>
                  {stats.top_offres.map((item, idx) => (
                    <tr key={idx}>
                      <td>{idx + 1}.</td>
                      <td>{item.description}</td>
                      <td className="text-end fw-bold" style={{ color: 'var(--accent-blue)' }}>{item.total}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          <div className="glass-card fade-up" style={{ animationDelay: `${getNextDelay()}ms` }}>
            <h5 className="mb-3 d-flex align-items-center gap-2" style={{ fontSize: 'clamp(0.95rem, 1.3vw, 1.1rem)' }}>
              <FontAwesomeIcon icon={faCalendarAlt} className="text-accent" />
              Derniers placements
            </h5>
            {stats.derniers_placements.length === 0 ? (
              <p className="text-secondary">Aucun placement récent</p>
            ) : (
              <table className="recent-table">
                <thead>
                  <tr>
                    <th>Candidat</th>
                    <th>Offre</th>
                    <th>Entreprise</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.derniers_placements.map((item, idx) => (
                    <tr key={idx}>
                      <td>{item.prenom} {item.nom}</td>
                      <td>{item.offre_description}</td>
                      <td>{item.nomEntreprise}</td>
                      <td>{new Date(item.date_placement).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {/* Toast */}
      {toast.show && (
        <div className="toast-container">
          <div className={`toast-item ${toast.variant}`}>
            <FontAwesomeIcon
              icon={toast.variant === 'success' ? faCheckCircle : faExclamationTriangle}
              className={toast.variant === 'success' ? 'text-success' : 'text-danger'}
            />
            <span>{toast.message}</span>
            <button className="btn-close-custom" onClick={() => setToast({ ...toast, show: false })}>✕</button>
          </div>
        </div>
      )}
    </>
  );
};

export default DashboardPlacements;